<template>
  <main-input-dialog
    v-bind="$props"
    :is-visible.sync="computedIsVisible"
    :title-translation="computedTitle"
    @modalClosed="onClose"
    dialog-width="65%"
    agree-text="dialog.agreeSave"
    :inputs="inputs"
    header-shadow
    class="pt-6"
    @agree="handleRule"
    :loading="isLoading"
  >
    <template #ruleName.input="slot">
      <v-col>
        <v-row no-gutters>
          <v-col cols="4">
            <data-input v-model="payload.name" v-bind="slot" v-on="$listeners"></data-input>
          </v-col>
        </v-row>
      </v-col>
    </template>
    <template #ruleText.input="slot">
      <v-col>
        <v-row no-gutters>
          <v-col cols="10">
            <data-input
              v-model="payload.rule_text"
              v-bind="slot"
              v-on="$listeners"
              :value="rule.rule_text"
            ></data-input>
          </v-col>
        </v-row>
      </v-col>
    </template>

    <template #info>
      <modeling-rule-info class="mt-n16" :is-simple="isSimple"></modeling-rule-info>
    </template>
  </main-input-dialog>
</template>

<script>
import ModelingRuleInfo from '@/components/ModelingRuleInfo';
import DataInput from '@/components/DataInput';
import { call, get } from 'vuex-pathify';

export default {
  name: 'ModelingRuleDialog',
  components: { ModelingRuleInfo, MainInputDialog: () => import('@/components/MainInputDialog'), DataInput },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    rule: {
      type: Object,
      default: () => {},
    },
  },
  data: vm => ({
    payload: {
      name: vm.rule.name || '',
      rule_text: vm.rule.rule_text || '',
    },
    isLoading: false,
  }),
  computed: {
    rules: get('epanet/rules'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    inputs() {
      return [
        {
          name: 'ruleName',
          translationPath: 'dialog.body.ruleName',
          clearable: true,
          leftCol: 3,
          rules: [v => !!v || v === undefined || 'rules.required'],
          dataType: {
            name: 'text',
            rules: [v => !!v || 'rules.required'],
          },
        },
        {
          name: 'ruleText',
          translationPath: 'dialog.body.ruleText',
          rules: [v => !!v || v === undefined || 'rules.required'],
          clearable: true,
          autoGrow: true,
          rows: 4,
          noResize: true,
          leftCol: 3,
          backgroundColor: '#f3f3f3',
          rowHeight: '30',
          dense: true,
          dataType: {
            name: this.rule.rule_type === 'control' ? 'text' : 'textarea',
            rules: [v => !!v || 'rules.required'],
          },
        },
      ];
    },
    computedPayload() {
      return {
        ...this.payload,
        rule_type: this.rule.rule_type,
      };
    },
    isSimple() {
      return this.rule.rule_type === 'control';
    },
    isNew() {
      return !this.rule.id;
    },
    computedTitle() {
      if (this.isSimple) {
        if (!this.isNew) {
          return `${this.$i18n.t('dialog.title.rule')}: ${this.rule.name}`;
        } else {
          return this.$i18n.t('dialog.title.addSimpleRule');
        }
      } else {
        if (!this.isNew) {
          return `${this.$i18n.t('dialog.title.rule')}: ${this.rule.name}`;
        } else {
          return this.$i18n.t('dialog.title.addAdvancedRule');
        }
      }
    },
    handleRule() {
      return this.isNew ? this.handleAdd : this.handleEdit;
    },
    nameAlreadyUsed() {
      return this.rules.filter(rule => rule.name !== this.rule.name).some(rule => rule.name === this.payload.name);
    },
  },
  methods: {
    addRule: call('epanet/addRule'),
    editRule: call('epanet/editRule'),
    onClose() {
      this.$emit('modalClosed');
    },
    async handleAdd() {
      this.isLoading = true;
      if (this.nameAlreadyUsed) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.ruleAlreadyExists'),
        });
        this.isLoading = false;
        return;
      }
      try {
        await this.addRule(this.computedPayload);
      } finally {
        this.isLoading = false;
        this.$emit('modalClosed');
        this.computedIsVisible = false;
      }
    },
    async handleEdit() {
      this.isLoading = true;
      const payload = { rule_id: this.rule.id, body: this.computedPayload };
      if (this.nameAlreadyUsed) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.ruleAlreadyExists'),
        });
        this.isLoading = false;
        return;
      }
      try {
        await this.editRule(payload);
      } finally {
        this.isLoading = false;
        this.$emit('modalClosed');
        this.computedIsVisible = false;
      }
    },
  },
};
</script>

<style scoped></style>
